import type { ItemPolicyEvaluationItemParams } from 'businessTraveller/types/itemPolicyEvaluation.types'
import { normalizeFlightIataAirportCodeForItemPolicy } from './normalizeFlightIataAirportCodeForItemPolicy'

// Builds the itemPolicyLookup object for POST /trip-requests and POST /orders. This is the base
// shape shared with the evaluate endpoint; it carries no key or cost.
export function buildItemPolicyLookup(
  params: ItemPolicyEvaluationItemParams,
): App.BusinessTraveller.ItemPolicyLookupItem {
  if (params.itemPolicyType === 'HOTEL') {
    // Location is exactly one of place-id or coordinates; prefer the place id when present.
    const location = params.lePlaceId
      ? { lePlaceId: params.lePlaceId }
      : { latitude: params.latitude ?? '', longitude: params.longitude ?? '' }
    return {
      itemPolicyType: 'HOTEL',
      currencyCode: params.currencyCode,
      numberOfNights: params.numberOfNights,
      roomCount: params.roomCount,
      location,
    }
  } else if (params.itemPolicyType === 'FLIGHT') {
    return {
      itemPolicyType: 'FLIGHT',
      currencyCode: params.currencyCode,
      departingAirportCode:
        normalizeFlightIataAirportCodeForItemPolicy(params.departingAirportCode) ?? params.departingAirportCode,
      arrivingAirportCode:
        normalizeFlightIataAirportCodeForItemPolicy(params.arrivingAirportCode) ?? params.arrivingAirportCode,
      flightViewType: params.flightViewType,
      travellerCount: params.travellerCount,
    }
  } else {
    return {
      itemPolicyType: 'CAR_HIRE',
      currencyCode: params.currencyCode,
      numberOfDays: params.numberOfDays,
      travellerCount: params.travellerCount,
    }
  }
}

// Builds the item payload for the POST /item-policies/evaluate endpoint: the lookup plus a
// correlation key and cost.
// The `cost` field:
//   - budget bar: left undefined (we still pass it; JSON.stringify drops undefined, so it never hits the wire)
//   - credit bar: set, to get the full verdict + credit back
export function buildItemPolicyEvaluationItem(
  params: ItemPolicyEvaluationItemParams,
): App.BusinessTraveller.ItemPolicyEvaluationItem {
  return {
    ...buildItemPolicyLookup(params),
    key: params.cacheKey,
    cost: params.cost,
  }
}

// Gate for the evaluation fetch: only fire once the vertical's required inputs are present.
export function hasRequiredItemEvaluationInputs(params: ItemPolicyEvaluationItemParams): boolean {
  if (params.itemPolicyType === 'HOTEL') {
    return !!(params.lePlaceId || (params.latitude && params.longitude))
  } else if (params.itemPolicyType === 'FLIGHT') {
    return !!(params.departingAirportCode && params.arrivingAirportCode)
  } else {
    // car hire
    return params.numberOfDays >= 1 && params.travellerCount >= 1
  }
}
